import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { IDatahubCaseDto } from 'api/cases/dtos';
import routes from 'app.routes';
import DownloadIcon from 'components/v2/icons/DownloadIcon';
import EyesIcon from 'components/v2/icons/EyesIcon';
import { useLocalization } from 'localization';
import { CaseMaterialStatus, IDatahubCase } from 'api';
import { MODAL_ID, MODALS_STORE } from 'constants/ui/modal';
import StatusChip from 'components/v2/chip/StatusChip';
import CasePresentationMenu from 'components/v2/shared/CasePresentationMenu';
import { GridRenderCellParams } from '@mui/x-data-grid';

export const latestCasesColumns = () => {
  const { t, f } = useLocalization();
  const navigate = useNavigate();
  const { toggleModal } = MODALS_STORE.cases(state => state);

  return [
    {
      headerName: t('common.terms.updatedDate'),
      field: 'modifiedDate',
      width: 200,
      sortable: false,
      valueGetter: (value: IDatahubCaseDto['modifiedDate']) => (value ? f(new Date(value), { date: 'short' }) : '-'),
    },
    {
      headerName: t('common.terms.case'),
      field: 'reference',
      width: 250,
      sortable: false,
    },
    {
      headerName: t('common.terms.address'),
      field: 'address',
      width: 300,
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<IDatahubCaseDto, IDatahubCaseDto['address']>) => {
        const value = params.value;
        return (
          <Typography variant="body1" height={'100%'} display="flex" alignItems="center">
            {value?.addressFull ?? '-'}
          </Typography>
        );
      },
    },
    {
      headerName: t('common.terms.storeOrBranch'),
      field: 'branchName',
      width: 300,
      sortable: false,
    },
    {
      headerName: t('common.terms.actions'),
      field: 'id',
      key: 'actions',
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<IDatahubCaseDto, IDatahubCaseDto['id']>) => {
        const record = params.row;
        const isDisableMaterial =
          !record.materialInfo?.hasMaterials || record.materialInfo.materialsState !== CaseMaterialStatus.ACTIVE;
        return (
          <Stack direction="row" gap={0.5} alignItems="center" justifyContent="center" height={'100%'}>
            <Tooltip title={t('common.actions.viewCase')}>
              <IconButton
                aria-label="view-case"
                disabled={!record.id}
                onClick={() => navigate(`${routes.v2.cases.overview}/${record.id}`)}
                sx={{ border: '1px solid' }}
                size="large"
              >
                <EyesIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('common.actions.getMaterials')}>
              <IconButton
                disabled={isDisableMaterial}
                aria-label="get-material"
                onClick={() => {
                  toggleModal(MODAL_ID.CASES_MATERIALS_DOWNLOAD, {
                    caseId: record.id,
                  });
                }}
                sx={{ border: '1px solid' }}
                size="large"
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <CasePresentationMenu disabledMaterial={isDisableMaterial} caseId={record.id} />
          </Stack>
        );
      },
    },
  ];
};

export const casesColumn = () => {
  const { t, f } = useLocalization();
  const navigate = useNavigate();
  const { toggleModal } = MODALS_STORE.cases(state => state);

  return [
    {
      headerName: t('common.terms.updatedDate'),
      field: 'modifiedDate',
      width: 170,
      valueGetter: (value: IDatahubCaseDto['modifiedDate']) => (value ? f(new Date(value), { date: 'short' }) : '-'),
    },
    {
      headerName: t('common.terms.case'),
      field: 'reference',
      width: 250,
    },
    {
      headerName: t('common.terms.address'),
      field: 'address',
      width: 250,
      flex: 1,
      minWidth: 200,
    },
    {
      headerName: t('common.terms.storeOrBranch'),
      field: 'departmentName',
      width: 250,
      sortable: false,
    },
    {
      headerName: t('common.terms.status'),
      field: 'orderStatus',
      width: 200,
      sortable: true,
      renderCell: (params: GridRenderCellParams<IDatahubCase, IDatahubCase['orderStatus']>) => {
        const rawStatus = params.value;
        return <StatusChip status={rawStatus ?? ''} />;
      },
    },
    {
      headerName: t('common.terms.actions'),
      field: 'id',
      sortable: false,
      width: 200,
      renderCell: (params: GridRenderCellParams<IDatahubCase, IDatahubCase['id']>) => {
        const record = params.row;
        const isDisableMaterial = !record.hasMaterials || record.materialStatus !== CaseMaterialStatus.ACTIVE;
        return (
          <Stack direction="row" gap={0.5} alignItems="center" justifyContent="center" height={'100%'}>
            <Tooltip title={t('common.actions.viewCase')}>
              <IconButton
                aria-label="view-case"
                disabled={!record.id}
                onClick={event => {
                  event.stopPropagation();
                  navigate(`${routes.v2.cases.overview}/${record.id}`);
                }}
                sx={{ border: '1px solid' }}
                size="large"
              >
                <EyesIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('common.actions.getMaterials')}>
              <IconButton
                disabled={isDisableMaterial}
                aria-label="get-material"
                onClick={event => {
                  event.stopPropagation();
                  toggleModal(MODAL_ID.CASES_MATERIALS_DOWNLOAD, {
                    caseId: record.id,
                  });
                }}
                sx={{ border: '1px solid' }}
                size="large"
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <CasePresentationMenu disabledMaterial={isDisableMaterial} caseId={record.id} />
          </Stack>
        );
      },
    },
  ];
};
